<template>
    <div>
        <div class="mb-4 flex items-center justify-between gap-2">
            <UInput v-model="search" type="text" placeholder="Search..." icon="i-ph-magnifying-glass-duotone" class="w-72" />
            <UButton v-if="canWrite" color="black" icon="i-ph-plus-bold" label="Add Equipment" @click="edit()" />
        </div>

        <UTable
            :rows="filtered"
            :columns="columns"
            :loading="loading"
            :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
            :empty-state="{ icon: 'i-ph-heartbeat-duotone', label: 'No equipment.' }"
            :ui="{ td: { padding: 'py-3' }, tr: { base: canWrite ? 'cursor-pointer hover:bg-gray-50' : '' } }"
            v-on="canWrite ? { select: edit } : {}"
        >
            <template #name-data="{ row }">
                <div class="min-w-[10rem] whitespace-normal leading-5">
                    <p>{{ row.name }}</p>
                    <span v-if="row.specification || storageName(row.storage_id)" class="block text-sm text-gray-500">
                        {{ [row.specification, storageName(row.storage_id)].filter(Boolean).join(' · ') }}
                    </span>
                    <span v-if="row.situation || row.action" class="block text-sm text-gray-500">
                        {{ [row.situation, row.action && `Action: ${row.action}`].filter(Boolean).join(' · ') }}
                    </span>
                </div>
            </template>
            <template #qty-data="{ row }">
                <p class="font-mono text-right">{{ row.qty }}</p>
            </template>
            <template #condition-data="{ row }">
                <BadgeEquipmentCondition :condition="row.condition" />
            </template>
            <template #actions-data="{ row }">
                <div @click.stop>
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </div>
            </template>
        </UTable>

        <OverlayFieldPostEditEquipment v-model:open="overlays.edit" :post="post" :equipment="selected" @reload="fetchData" />
        <OverlayFieldPostRemoveEquipment v-model:open="overlays.remove" :equipment="selected" @reload="fetchData" />
    </div>
</template>

<script setup lang="ts">
import { EquipmentCondition, type FieldPost, type FieldPostEquipment } from '~/types/fieldPost'

const props = defineProps<{
    post: FieldPost
    canWrite: boolean
}>()

const client = useSupabaseClient()

const loading = ref(true)
const search = ref('')
const equipment = ref<FieldPostEquipment[]>([])
const selected = ref<FieldPostEquipment>()
const overlays = reactive({ edit: false, remove: false })

const columns = computed(() => [
    { key: 'name', label: 'Name' },
    { key: 'qty', label: 'Qty', class: 'text-right' },
    { key: 'condition', label: 'Condition', class: 'w-px' },
    ...(props.canWrite ? [{ key: 'actions', class: 'w-px' }] : []),
])

const conditionOrder = [EquipmentCondition.outOfService, EquipmentCondition.needsAttention, EquipmentCondition.ok]

const filtered = computed(() => {
    const q = search.value.trim().toLowerCase()
    return equipment.value
        .filter(e => !q || `${e.name} ${e.specification ?? ''} ${e.situation ?? ''}`.toLowerCase().includes(q))
        .sort((a, b) => conditionOrder.indexOf(a.condition) - conditionOrder.indexOf(b.condition) || a.name.localeCompare(b.name))
})

function storageName(id: string | null) {
    return props.post.storages?.find(s => s.id === id)?.name
}

function actions(e: FieldPostEquipment) {
    return [
        [
            { label: 'Edit', icon: 'i-ph-pencil-fill', click: () => edit(e) },
            {
                label: 'Remove',
                icon: 'i-ph-trash-fill',
                iconClass: 'text-red-400',
                click: () => {
                    selected.value = e
                    overlays.remove = true
                },
            },
        ],
    ]
}

function edit(e?: FieldPostEquipment) {
    selected.value = e
    overlays.edit = true
}

onMounted(() => fetchData())

async function fetchData() {
    loading.value = true
    const { data } = await client.from('field_post_equipment').select('*').eq('post_id', props.post.id)
    equipment.value = (data ?? []) as FieldPostEquipment[]
    loading.value = false
}
</script>
